import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Markdown,
    Title,
    Loader
} from "dash-styled"
import { ReactComponent as ArrowDown } from "icons/lib/arrow-down.svg"
import { FC, useEffect } from "react"

import { useAppSuperTasks } from "../../../../../../../../../../../Hooks/useAppSuperTasks"
import {
    SuperTaskModel,
    SuperTaskParametersModel,
    SuperTaskWorkerModel
} from "../../../../../../../../../../../Models/API/SuperTaskModel"
import { SettingsSection } from "../../../../../../Common/SettingsSection"
import TaskParameters from "./Parameters"
import TaskWorkers from "./Workers"

type TasksSectionProps = {
    appID: string
    tasks?: SuperTaskModel[]
    disabled?: boolean
    onChange: (formData: unknown) => void
    setChanged: (state: boolean) => void
}

export const TasksSection: FC<TasksSectionProps> = ({
    appID,
    tasks,
    disabled = false,
    onChange,
    setChanged
}) => {
    const { value: initialValue, loading } = useAppSuperTasks(appID)

    const superTasksSettingsChanged =
        initialValue &&
        initialValue.length > 0 &&
        tasks !== undefined &&
        JSON.stringify(tasks) !== JSON.stringify(initialValue)

    useEffect(() => {
        setChanged(!!superTasksSettingsChanged)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [superTasksSettingsChanged])

    useEffect(() => {
        onChange(initialValue)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValue])

    const handleParamsChange =
        (taskIndex: number) => (parameters: SuperTaskParametersModel) => {
            if (!disabled) {
                const newTasks = [...(tasks || [])]
                newTasks.splice(taskIndex, 1, {
                    ...newTasks[taskIndex],
                    parameters
                })
                onChange(newTasks)
            }
        }

    const handleWorkersChange =
        (taskIndex: number) => (workers: SuperTaskWorkerModel[]) => {
            if (!disabled) {
                const newTasks = [...(tasks || [])]
                newTasks.splice(taskIndex, 1, {
                    ...newTasks[taskIndex],
                    workers
                })
                onChange(newTasks)
            }
        }

    if (loading) {
        return <Loader />
    }

    return tasks?.length && tasks.length > 0 ? (
        <SettingsSection title="Tasks to work on" titleComponent="h3">
            <Markdown
                text="These are the tasks that workers actually work on. You can change some basic settings of the tasks."
                className="mb-12"
            />
            {tasks?.map(
                (
                    {
                        superTaskWorkflow,
                        workers,
                        workersSchema,
                        parametersSchema,
                        parameters,
                        editable = true
                    },
                    taskIndex
                ) => {
                    const title = superTaskWorkflow.split(".")[1]
                    return (
                        <Accordion
                            key={taskIndex}
                            elevation={0}
                            variant="outlined"
                            className="rounded-lg mb-4"
                        >
                            <AccordionSummary
                                className="flex-row-reverse border-smoke-320 min-h-13"
                                expandIcon={<ArrowDown />}
                            >
                                <Title
                                    component="h4"
                                    variant="heading4"
                                    className="capitalize w-full m-0 ml-5"
                                >
                                    {title}
                                </Title>
                            </AccordionSummary>
                            <AccordionDetails className="block bg-rhino-200 shadow-inner p-6">
                                <TaskParameters
                                    onChange={handleParamsChange(taskIndex)}
                                    parameters={parameters}
                                    schema={parametersSchema}
                                    disabled={disabled || !editable}
                                />
                                <TaskWorkers
                                    schema={workersSchema}
                                    workers={workers}
                                    disabled={disabled || !editable}
                                    onChange={handleWorkersChange(taskIndex)}
                                />
                            </AccordionDetails>
                        </Accordion>
                    )
                }
            )}
        </SettingsSection>
    ) : null
}
